import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Truck, 
  Phone,
  MapPin,
  Utensils,
  Volume2,
  VolumeX,
  RefreshCw,
  Printer,
  ChevronRight,
  Timer,
  Package,
  AlertCircle,
  Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { orderAPI, printerAPI, api } from '../lib/api';
import { formatPrice, formatDateTime, printOrderTicket } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Preparation time options in minutes (ASAP orders)
const PREP_TIME_OPTIONS = [
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
  { value: 20, label: '20 min' },
  { value: 25, label: '25 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
  { value: 90, label: '90 min' },
];

// Compute prep warning window: order is "due soon" if now >= scheduled_for - prep_warning_minutes
const getScheduledInfo = (order, prepWarningMinutes = 10) => {
  if (order?.scheduled_type !== 'scheduled' || !order?.scheduled_for) {
    return { isScheduled: false, isDueSoon: false, isOverdue: false, label: '' };
  }
  const scheduledDate = new Date(order.scheduled_for);
  const now = new Date();
  const diffMin = (scheduledDate.getTime() - now.getTime()) / 60000;
  const isDueSoon = diffMin <= prepWarningMinutes && diffMin > -5; // within warning window
  const isOverdue = diffMin <= -5;
  const isToday = scheduledDate.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = scheduledDate.toDateString() === tomorrow.toDateString();
  const timeStr = scheduledDate.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
  const dayStr = isToday ? 'Vandaag' : isTomorrow ? 'Morgen' : scheduledDate.toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit' });
  return {
    isScheduled: true,
    isDueSoon,
    isOverdue,
    label: `${dayStr} · ${timeStr}`,
    minutesUntil: Math.round(diffMin),
  };
};

// Scheduled order badge — orange normally, red+pulse when due soon
const ScheduledBadge = ({ order, prepWarningMinutes = 10, size = 'sm' }) => {
  const info = getScheduledInfo(order, prepWarningMinutes);
  if (!info.isScheduled) return null;
  const isSm = size === 'sm';
  const base = isSm ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1';
  const color = info.isDueSoon || info.isOverdue
    ? 'bg-red-500 text-white animate-pulse'
    : 'bg-orange-100 text-orange-800 border border-orange-300';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${base} ${color}`} data-testid={`scheduled-badge-${order.id}`}>
      <Clock className={isSm ? 'w-3 h-3' : 'w-4 h-4'} />
      {info.isOverdue ? 'TE LAAT · ' : info.isDueSoon ? 'NU BEREIDEN · ' : 'Gepland · '}
      {info.label}
    </span>
  );
};

// Notification sound URL - using a bell/ding sound
const NOTIFICATION_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

// Multiple beeps for attention
const playNotificationSound = () => {
  try {
    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.volume = 1.0;
    
    // Play sound 3 times with intervals for more attention
    const playSequence = async () => {
      for (let i = 0; i < 3; i++) {
        audio.currentTime = 0;
        await audio.play().catch(() => {});
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    };
    
    playSequence();
  } catch (e) {
    console.log('Audio not supported, trying fallback');
    // Fallback: use Web Audio API for a simple beep
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContext();
      
      const playBeep = () => {
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.5, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);
        oscillator.start(audioCtx.currentTime);
        oscillator.stop(audioCtx.currentTime + 0.5);
      };
      
      playBeep();
      setTimeout(playBeep, 600);
      setTimeout(playBeep, 1200);
    } catch (e2) {
      console.log('Web Audio API not supported');
    }
  }
};

export const LiveOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  // Gevuld zodra een terugbetaling mislukt; stuurt de tweede dialoog aan.
  const [mislukteTerugbetaling, setMislukteTerugbetaling] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);  // order to show in OrderDetailModal
  const [selectedPrepTime, setSelectedPrepTime] = useState(20);
  // For scheduled orders: minute adjustment vs. customer's requested time (0 = confirm exactly, ±5/10/15)
  const [scheduledAdjustment, setScheduledAdjustment] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [printerApiKey, setPrinterApiKey] = useState('');
  // Print mode (shared via localStorage with Print Station): 'browser' or 'rawbt'.
  // 'rawbt' = directly trigger Android RawBT intent for fully silent ESC/POS print
  const [printMode, setPrintMode] = useState(() => {
    try { return localStorage.getItem('foodflows_print_station_mode') || 'browser'; } catch { return 'browser'; }
  });
  const { restaurant } = useAuth();
  const previousOrdersRef = useRef([]);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  // Tick every 30s so scheduled-order badges re-evaluate their "due soon" state without polling backend
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  // Reference tick so linter knows it's intentional
  void tick;

  // Persist print mode change
  useEffect(() => {
    try { localStorage.setItem('foodflows_print_station_mode', printMode); } catch { /* ignore */ }
  }, [printMode]);

  // Fetch printer api key once on mount (used by Print Station URL & RawBT)
  useEffect(() => {
    printerAPI.getConfig().then((res) => {
      if (res.data?.printer_api_key) setPrinterApiKey(res.data.printer_api_key);
    }).catch(() => {});
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if (!('Notification' in window)) return undefined;
    // Defer state update to next microtask to satisfy react-hooks rules
    const t = setTimeout(() => {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission);
        });
      }
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // ============== WEB PUSH NOTIFICATIONS ==============
  // Allows the restaurant to receive new-order alerts even when the
  // browser/tab is closed (works on Android & desktop Chrome via Service Worker).
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const out = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) out[i] = rawData.charCodeAt(i);
    return out;
  };

  // Reflect current push state on mount (sync with browser registration)
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      try {
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        setPushEnabled(!!existing);
      } catch { /* ignore */ }
    }, 100);
    return () => clearTimeout(t);
  }, []);

  const enablePushNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Push notificaties worden niet ondersteund in deze browser');
      return;
    }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast.error('Geef toestemming voor meldingen om push te activeren');
        setPushBusy(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      // Re-use existing subscription if available
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        // Fetch public key from backend (single source of truth)
        const keyRes = await api.get('/push/public-key');
        const publicKey = keyRes.data?.public_key || process.env.REACT_APP_VAPID_PUBLIC_KEY;
        if (!publicKey) {
          toast.error('Server VAPID-sleutel niet beschikbaar');
          setPushBusy(false);
          return;
        }
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      const subJson = sub.toJSON();
      await api.post('/push/subscribe', {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });
      setPushEnabled(true);
      // Trigger a server-side test push so user immediately sees it works
      await api.post('/push/test');
      toast.success('Push notificaties geactiveerd — je krijgt nu meldingen bij nieuwe bestellingen');
    } catch (e) {
      console.error('[Push] enable failed', e);
      toast.error('Kon push notificaties niet activeren');
    } finally {
      setPushBusy(false);
    }
  };

  const disablePushNotifications = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setPushEnabled(false);
      toast.success('Push notificaties uitgeschakeld');
    } catch (e) {
      console.error('[Push] disable failed', e);
    } finally {
      setPushBusy(false);
    }
  };

  // Show browser notification
  const showBrowserNotification = (count) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('FoodFlows - Nieuwe Bestelling!', {
        body: `${count} nieuwe bestelling${count > 1 ? 'en' : ''} ontvangen`,
        icon: '/logo192.png',
        tag: 'new-order',
        requireInteraction: true,
        vibrate: [200, 100, 200]
      });
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      const response = await orderAPI.getAll();
      const allOrders = response.data;
      
      // Show ALL active orders (never hide active orders behind a date filter — 
      // otherwise late-night orders vanish at midnight before restaurant sees them).
      // Only completed/cancelled/rejected orders are filtered to TODAY.
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const ACTIVE_STATUSES = ['pending', 'confirmed', 'preparing', 'ready', 'queued', 'in_queue'];
      const newOrders = allOrders.filter(o => {
        if (ACTIVE_STATUSES.includes(o.status)) return true;
        const created = new Date(o.created_at);
        return created >= today;
      });
      
      // Check for new pending orders (compare with previous)
      const newPendingOrders = newOrders.filter(
        order => order.status === 'pending' && 
        !previousOrdersRef.current.find(o => o.id === order.id)
      );
      
      // IMPORTANT: Update UI FIRST so new orders show immediately, THEN play sound
      previousOrdersRef.current = newOrders;
      setOrders(newOrders);
      setLastFetch(new Date());
      
      if (newPendingOrders.length > 0 && previousOrdersRef.current.length > 0) {
        // Play sound if enabled
        if (soundEnabled) {
          playNotificationSound();
        }
        
        // Show browser notification
        showBrowserNotification(newPendingOrders.length);
        
        // Show toast
        toast.info(`${newPendingOrders.length} nieuwe bestelling(en)!`, {
          icon: <Bell className="w-4 h-4" />,
          duration: 10000,
        });
        
        // Flash the page title
        const originalTitle = document.title;
        let flashCount = 0;
        const titleFlash = setInterval(() => {
          document.title = flashCount % 2 === 0 ? '🔔 NIEUWE BESTELLING!' : originalTitle;
          flashCount++;
          if (flashCount > 10) {
            clearInterval(titleFlash);
            document.title = originalTitle;
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [soundEnabled]);

  useEffect(() => {
    // Defer initial fetch so React doesn't flag synchronous setState during render
    const initial = setTimeout(fetchOrders, 0);
    // Poll every 3 seconds for new orders (fast refresh for busy peak hours)
    const interval = setInterval(fetchOrders, 3000);
    
    return () => {
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, [fetchOrders]);

  // Triggers a direct browser print via hidden iframe + window.print().
  // Opens the receipt HTML in an offscreen iframe and immediately calls
  // print() on its contentWindow — no tab switch needed.
  const printViaBrowser = (orderId) => {
    if (!printerApiKey) return false;
    return new Promise((resolve) => {
      let iframe = document.getElementById('foodflows-print-iframe');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'foodflows-print-iframe';
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);
      }
      const url = `${baseUrl}/api/print/station/${printerApiKey}/receipt/${orderId}?t=${Date.now()}`;
      let done = false;
      const finish = (ok) => { if (done) return; done = true; resolve(ok); };
      const onLoad = () => {
        iframe.removeEventListener('load', onLoad);
        // Small delay so receipt fully paints before printing
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            finish(true);
          } catch (e) {
            console.error('[LiveOrders] browser print failed', e);
            finish(false);
          }
        }, 400);
      };
      iframe.addEventListener('load', onLoad);
      setTimeout(() => finish(false), 10000);
      iframe.src = url;
    });
  };

  // Triggers a direct RawBT silent print on Android.
  // Returns true if intent was dispatched, false on error.
  const printViaRawBT = async (orderId) => {
    if (!printerApiKey) return false;
    try {
      const res = await fetch(`${baseUrl}/api/print/station/${printerApiKey}/rawbt/${orderId}`);
      if (!res.ok) return false;
      const data = await res.json();
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = data.intent_url || data.rawbt_url;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => a.remove(), 1000);
      return true;
    } catch (e) {
      console.error('[LiveOrders] RawBT print failed', e);
      return false;
    }
  };

  const handleAcceptOrder = async () => {
    if (!selectedOrder) return;
    
    setProcessing(true);
    try {
      const isScheduled = selectedOrder.scheduled_type === 'scheduled' && selectedOrder.scheduled_for;
      let prepMinutes = selectedPrepTime;
      let successLabel = `Klaar over ${selectedPrepTime} min`;
      if (isScheduled) {
        // Compute minutes-until-final-time (customer's chosen time + adjustment)
        const finalTime = new Date(selectedOrder.scheduled_for).getTime() + (scheduledAdjustment * 60000);
        prepMinutes = Math.max(1, Math.round((finalTime - Date.now()) / 60000));
        const finalHHMM = new Date(finalTime).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
        successLabel = `Bevestigd voor ${finalHHMM}`;
      }
      // Update status (also sends customer email)
      await orderAPI.updateStatus(selectedOrder.id, 'confirmed', prepMinutes);
      // Auto-print: RawBT (silent), Browser (direct print dialog), of fallback queue
      let printToastSuffix = '';
      if (printMode === 'rawbt' && printerApiKey) {
        const ok = await printViaRawBT(selectedOrder.id);
        printToastSuffix = ok ? ' · Bon stil geprint via RawBT' : ' · Print mislukt';
        await api.post(`/orders/${selectedOrder.id}/queue-print`).catch(() => {});
      } else if (printerApiKey) {
        const ok = await printViaBrowser(selectedOrder.id);
        printToastSuffix = ok ? ' · Print dialoog geopend' : ' · Print mislukt';
        await api.post(`/orders/${selectedOrder.id}/queue-print`).catch(() => {});
      } else {
        await api.post(`/orders/${selectedOrder.id}/queue-print`).catch(() => {});
        printToastSuffix = ' · Bon verstuurd naar Print Station';
      }
      toast.success(`Bestelling geaccepteerd · ${successLabel}${printToastSuffix}`);
      setShowAcceptDialog(false);
      setSelectedOrder(null);
      setScheduledAdjustment(0);
      fetchOrders();
    } catch (error) {
      toast.error('Kon bestelling niet accepteren');
    } finally {
      setProcessing(false);
    }
  };

  // Weigeren en annuleren zijn hetzelfde: de bestelling gaat niet door en een
  // online betaalde klant hoort zijn geld terug te krijgen. Dat liep eerder
  // langs twee verschillende endpoints, waarvan er een niet terugbetaalde.
  const annuleerBestelling = async (orderId, { zonderTerugbetaling = false } = {}) => {
    const ord = orders.find(o => o.id === orderId) || selectedOrder || detailOrder;
    setProcessing(true);
    try {
      const res = await orderAPI.cancelWithRefund(orderId, zonderTerugbetaling);
      const info = res.data?.refund;
      if (info?.refunded) {
        toast.success(`Geannuleerd \u00b7 \u20ac${info.amount.toFixed(2)} terugbetaald via Stripe`,
                      { duration: 8000 });
      } else if (info?.handmatig_nodig) {
        toast.warning('Geannuleerd zonder terugbetaling. Betaal de klant zelf terug via Stripe.',
                      { duration: 15000 });
      } else {
        toast.success('Bestelling geannuleerd');
      }
      setShowRejectDialog(false);
      setSelectedOrder(null);
      setDetailOrder(null);
      setMislukteTerugbetaling(null);
      fetchOrders();
      return true;
    } catch (e) {
      const melding = e?.response?.data?.detail || 'Kon bestelling niet annuleren';
      // 409 betekent: de terugbetaling is niet gelukt, dus de bestelling staat
      // er nog. Dat moet de eigenaar expliciet te zien krijgen, met de keuze om
      // alsnog zonder terugbetaling af te sluiten.
      if (e?.response?.status === 409 && !zonderTerugbetaling) {
        setMislukteTerugbetaling({ orderId, melding, bedrag: ord?.total, nummer: ord?.order_number });
      } else {
        toast.error(melding, { duration: 12000 });
      }
      return false;
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectOrder = async () => {
    if (!selectedOrder) return;
    await annuleerBestelling(selectedOrder.id);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await orderAPI.updateStatus(orderId, newStatus);
      toast.success(`Status bijgewerkt naar ${getStatusLabel(newStatus)}`);
      fetchOrders();
    } catch (error) {
      toast.error('Kon status niet bijwerken');
    }
  };

  const handlePrint = async (orderId) => {
    // RawBT mode: direct silent print on Android (no Print Station tablet needed)
    if (printMode === 'rawbt' && printerApiKey) {
      const ok = await printViaRawBT(orderId);
      if (ok) {
        toast.success('Bon stil geprint via RawBT');
        // Still mark as queued so Print Station won't re-print the same order
        await api.post(`/orders/${orderId}/queue-print`).catch(() => {});
        return;
      }
      toast.error('RawBT print mislukt — controleer of RawBT app actief is');
      return;
    }
    // Browser mode: direct print via hidden iframe — no Print Station tab needed
    if (printerApiKey) {
      const ok = await printViaBrowser(orderId);
      if (ok) {
        toast.success('Print dialoog geopend');
        await api.post(`/orders/${orderId}/queue-print`).catch(() => {});
        return;
      }
    }
    // Last-resort fallback: open the receipt in a popup
    try {
      await api.post(`/orders/${orderId}/queue-print`);
      printOrderTicket(orderId);
      toast.success('Bon geopend in nieuw venster');
    } catch (error) {
      printOrderTicket(orderId);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'In afwachting',
      confirmed: 'Bevestigd',
      preparing: 'In bereiding',
      ready: 'Klaar',
      delivered: 'Bezorgd',
      cancelled: 'Geannuleerd'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-500',
      confirmed: 'bg-blue-500',
      preparing: 'bg-purple-500',
      ready: 'bg-emerald-500',
      delivered: 'bg-gray-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getOrderTypeIcon = (type) => {
    return type === 'delivery' ? Truck : Utensils;
  };

  // Separate orders by status
  // Sort pending: scheduled orders that are due soon first, then normal FIFO
  const pendingOrders = orders
    .filter(o => o.status === 'pending')
    .sort((a, b) => {
      // Scheduled orders due soon get priority
      const aInfo = a.scheduled_type === 'scheduled' && a.scheduled_for
        ? new Date(a.scheduled_for).getTime() : null;
      const bInfo = b.scheduled_type === 'scheduled' && b.scheduled_for
        ? new Date(b.scheduled_for).getTime() : null;
      if (aInfo && bInfo) return aInfo - bInfo;
      if (aInfo) return -1;
      if (bInfo) return 1;
      return new Date(a.created_at) - new Date(b.created_at);
    });
  const activeOrders = orders.filter(o => ['confirmed', 'preparing'].includes(o.status));
  const readyOrders = orders.filter(o => o.status === 'ready');
  const completedOrders = orders.filter(o => ['delivered', 'cancelled'].includes(o.status)).slice(0, 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Bestellingen</h1>
          <p className="text-gray-600 mt-1">
            {lastFetch && `Laatst bijgewerkt: ${lastFetch.toLocaleTimeString('nl-BE')}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Web push subscribe toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={pushEnabled ? disablePushNotifications : enablePushNotifications}
            disabled={pushBusy}
            className={pushEnabled ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : ''}
            data-testid="push-toggle-btn"
            title={pushEnabled ? 'Push notificaties zijn actief — klik om uit te schakelen' : 'Klik om push notificaties te activeren'}
          >
            <Bell className="w-4 h-4 mr-1.5" />
            {pushEnabled ? 'Push Aan' : 'Push Uit'}
          </Button>

          {/* Print mode toggle — Browser vs RawBT (Android silent) */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPrintMode(m => m === 'rawbt' ? 'browser' : 'rawbt')}
            className={printMode === 'rawbt' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : ''}
            data-testid="print-mode-toggle"
            title={printMode === 'rawbt' ? 'RawBT (Android — stil)' : 'Browser print'}
          >
            <Printer className="w-4 h-4 mr-1.5" />
            {printMode === 'rawbt' ? 'RawBT' : 'Browser'}
          </Button>

          {/* Notification permission button */}
          {notificationPermission !== 'granted' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if ('Notification' in window) {
                  Notification.requestPermission().then(permission => {
                    setNotificationPermission(permission);
                    if (permission === 'granted') {
                      toast.success('Notificaties ingeschakeld!');
                    }
                  });
                }
              }}
              className="text-amber-600 border-amber-300"
              data-testid="enable-notifications-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Notificaties Inschakelen
            </Button>
          )}
          
          {/* Test sound button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              playNotificationSound();
              toast.info('Test geluid afgespeeld');
            }}
            title="Test geluid"
          >
            <Bell className="w-5 h-5" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={soundEnabled ? 'text-emerald-600' : 'text-gray-400'}
            data-testid="sound-toggle"
            title={soundEnabled ? 'Geluid uit' : 'Geluid aan'}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchOrders}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Vernieuwen
          </Button>
        </div>
      </div>

      {/* Pending Orders - Most Important */}
      {pendingOrders.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse"></div>
            <h2 className="text-lg font-semibold text-gray-900">
              Nieuwe Bestellingen ({pendingOrders.length})
            </h2>
          </div>
          <div className="grid gap-4">
            {pendingOrders.map(order => (
              <PendingOrderCard
                key={order.id}
                order={order}
                prepWarningMinutes={restaurant?.scheduled_orders_prep_warning_minutes || 10}
                onAccept={() => {
                  setSelectedOrder(order);
                  setShowAcceptDialog(true);
                }}
                onReject={() => {
                  setSelectedOrder(order);
                  setShowRejectDialog(true);
                }}
                onPrint={() => handlePrint(order.id)}
                onOpenDetail={setDetailOrder}
              />
            ))}
          </div>
        </div>
      )}

      {/* No Pending Orders Message */}
      {pendingOrders.length === 0 && (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Geen nieuwe bestellingen</h3>
          <p className="text-gray-500">Nieuwe bestellingen verschijnen hier automatisch</p>
        </div>
      )}

      {/* Active Orders */}
      {activeOrders.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            In Behandeling ({activeOrders.length})
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeOrders.map(order => (
              <ActiveOrderCard
                key={order.id}
                order={order}
                onStatusChange={handleUpdateStatus}
                onPrint={() => handlePrint(order.id)}
                onOpenDetail={setDetailOrder}
              />
            ))}
          </div>
        </div>
      )}

      {/* Ready Orders */}
      {readyOrders.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Klaar voor Afhalen/Bezorging ({readyOrders.length})
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {readyOrders.map(order => (
              <ReadyOrderCard
                key={order.id}
                order={order}
                onComplete={() => handleUpdateStatus(order.id, 'delivered')}
                onOpenDetail={setDetailOrder}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent Completed */}
      {completedOrders.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-700">
            Recent Afgerond
          </h2>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="space-y-1">
              {completedOrders.map(order => {
                const isDelivery = order.order_type === 'delivery';
                const addr = order.delivery_address;
                const primary = isDelivery && addr
                  ? `${addr.street || ''} ${addr.house_number || ''}${addr.city ? ', ' + addr.city : ''}`.trim()
                  : (order.customer_name || `#${order.order_number}`);
                return (
                  <div
                    key={order.id}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { if (e.target.closest('button')) return; setDetailOrder(order); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.target.closest('button')) setDetailOrder(order); }}
                    className="flex items-center justify-between gap-3 py-2.5 px-2 -mx-2 rounded-lg border-b border-gray-100 last:border-0 cursor-pointer hover:bg-white hover:shadow-sm transition-all"
                    data-testid={`completed-order-row-${order.order_number}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${order.status === 'delivered' ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded shrink-0 ${isDelivery ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'}`}>
                        {isDelivery ? 'Bezorgen' : 'Afhalen'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate">{primary}</p>
                        <p className="text-xs text-gray-500 truncate">
                          #{order.order_number} · {getStatusLabel(order.status)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-medium text-gray-700">{formatPrice(order.total, order.currency)}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handlePrint(order.id); }}
                        className="text-gray-500 hover:text-[#1A4D2E] hover:bg-emerald-50 h-8 w-8 p-0"
                        title="Bon opnieuw printen"
                        data-testid={`completed-print-btn-${order.order_number}`}
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Accept Order Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={(v) => { setShowAcceptDialog(v); if (!v) setScheduledAdjustment(0); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bestelling Accepteren</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (() => {
            const isScheduled = selectedOrder.scheduled_type === 'scheduled' && selectedOrder.scheduled_for;
            const isDelivery = selectedOrder.order_type === 'delivery';
            const addr = selectedOrder.delivery_address;
            const primary = isDelivery && addr
              ? `${addr.street || ''} ${addr.house_number || ''}${addr.city ? ', ' + addr.city : ''}`.trim()
              : (selectedOrder.customer_name || `#${selectedOrder.order_number}`);
            const requestedTime = isScheduled ? new Date(selectedOrder.scheduled_for) : null;
            const finalTime = isScheduled ? new Date(requestedTime.getTime() + scheduledAdjustment * 60000) : null;
            const requestedHHMM = requestedTime ? requestedTime.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' }) : '';
            const finalHHMM = finalTime ? finalTime.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' }) : '';

            return (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-lg truncate max-w-[220px]" title={primary}>{primary}</span>
                    <span className="font-bold text-lg shrink-0">{formatPrice(selectedOrder.total, selectedOrder.currency)}</span>
                  </div>
                  <p className="text-sm text-gray-500">#{selectedOrder.order_number} · {isDelivery ? 'Bezorging' : 'Afhalen'} · {selectedOrder.items?.length || 0} items</p>
                </div>

                {isScheduled ? (
                  // ==== SCHEDULED ORDER: confirm chosen time, offer ±min adjustments ====
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline mr-2" />
                      Klant wenst {isDelivery ? 'bezorging' : 'afhalen'} om
                    </label>
                    <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4 text-center mb-4">
                      <p className="text-3xl font-bold text-orange-900" data-testid="scheduled-requested-time">{requestedHHMM}</p>
                      <p className="text-xs text-orange-700 mt-1">
                        {new Date(selectedOrder.scheduled_for).toLocaleDateString('nl-BE', { weekday: 'long', day: '2-digit', month: 'short' })}
                      </p>
                    </div>

                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bevestig tijdstip of pas aan
                    </label>
                    <div className="grid grid-cols-5 gap-1.5 mb-3">
                      {[-15, -10, 0, 10, 15].map((adj) => (
                        <button
                          key={adj}
                          type="button"
                          onClick={() => setScheduledAdjustment(adj)}
                          className={`py-2 px-1 rounded-lg text-xs font-medium transition-all ${
                            scheduledAdjustment === adj
                              ? 'bg-emerald-500 text-white shadow'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`adjust-${adj}`}
                        >
                          {adj === 0 ? 'Exact op tijd' : (adj > 0 ? `+${adj} min` : `${adj} min`)}
                        </button>
                      ))}
                    </div>

                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                      <p className="text-xs text-emerald-700 uppercase tracking-wider font-medium">Klant krijgt bevestiging voor</p>
                      <p className="text-2xl font-bold text-emerald-900" data-testid="scheduled-final-time">{finalHHMM}</p>
                    </div>
                  </div>
                ) : (
                  // ==== ASAP ORDER: standard prep-time selector ====
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <Timer className="w-4 h-4 inline mr-2" />
                      Bereidingstijd
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {PREP_TIME_OPTIONS.map(option => (
                        <button
                          key={option.value}
                          onClick={() => setSelectedPrepTime(option.value)}
                          className={`py-3 px-4 rounded-xl font-medium transition-all ${
                            selectedPrepTime === option.value
                              ? 'bg-emerald-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowAcceptDialog(false)} disabled={processing}>
              Annuleren
            </Button>
            <Button 
              onClick={handleAcceptOrder} 
              disabled={processing}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="confirm-accept-btn"
            >
              {processing ? 'Bezig...' : (() => {
                const isScheduled = selectedOrder?.scheduled_type === 'scheduled' && selectedOrder?.scheduled_for;
                if (isScheduled) {
                  const finalTime = new Date(new Date(selectedOrder.scheduled_for).getTime() + scheduledAdjustment * 60000);
                  const hhmm = finalTime.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
                  return <><CheckCircle className="w-4 h-4 mr-2" />Bevestigen ({hhmm})</>;
                }
                return <><CheckCircle className="w-4 h-4 mr-2" />Accepteren ({selectedPrepTime} min)</>;
              })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Order Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Bestelling Weigeren</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900">
                      Weet je zeker dat je bestelling #{selectedOrder.order_number} wilt weigeren?
                    </p>
                    {/* Bij een online betaalde bestelling hoort hier het bedrag
                        te staan: anders weigert iemand een betaalde bestelling
                        zonder door te hebben dat er geld terugmoet. */}
                    <p className="text-sm text-red-700 mt-1">
                      {selectedOrder.payment_status === 'paid' && selectedOrder.payment_method === 'online'
                        ? `Het bedrag van \u20ac${selectedOrder.total?.toFixed(2)} wordt automatisch terugbetaald via Stripe. De klant ontvangt hiervan een melding.`
                        : 'De klant ontvangt een melding dat de bestelling is geannuleerd.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={processing}>
              Annuleren
            </Button>
            <Button 
              onClick={handleRejectOrder} 
              disabled={processing}
              variant="destructive"
              data-testid="confirm-reject-btn"
            >
              {processing ? 'Bezig...' : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Weigeren
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Modal — opens on any order-card click for full info view */}
      <OrderDetailModal
        order={detailOrder}
        open={!!detailOrder}
        onClose={() => setDetailOrder(null)}
        onPrint={handlePrint}
        onCancel={(orderId) => {
          const ord = orders.find(o => o.id === orderId) || detailOrder;
          const online = ord?.payment_status === 'paid' && ord?.payment_method === 'online';
          const vraag = online
            ? `Bestelling #${ord?.order_number} annuleren? Het bedrag van \u20ac${ord?.total?.toFixed(2)} wordt automatisch terugbetaald via Stripe.`
            : `Bestelling #${ord?.order_number} annuleren?`;
          if (!window.confirm(vraag)) return;
          annuleerBestelling(orderId);
        }}
      />

      {/* Tweede stap, alleen na een mislukte terugbetaling. De bestelling staat
          op dat moment nog gewoon open -- hij wordt pas afgesloten als de
          eigenaar hier bewust voor kiest. */}
      <Dialog open={!!mislukteTerugbetaling} onOpenChange={(v) => !v && setMislukteTerugbetaling(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Terugbetaling niet gelukt</DialogTitle>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
            <p className="text-sm text-red-900">{mislukteTerugbetaling?.melding}</p>
            <p className="text-sm text-red-700">
              Bestelling #{mislukteTerugbetaling?.nummer} is <strong>niet</strong> geannuleerd en
              staat nog in je lijst. Je kunt het zo opnieuw proberen.
            </p>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setMislukteTerugbetaling(null)} disabled={processing}>
              Laat staan
            </Button>
            <Button
              variant="outline"
              onClick={() => annuleerBestelling(mislukteTerugbetaling.orderId)}
              disabled={processing}
              data-testid="refund-retry-btn"
            >
              Opnieuw proberen
            </Button>
            <Button
              variant="destructive"
              onClick={() => annuleerBestelling(mislukteTerugbetaling.orderId, { zonderTerugbetaling: true })}
              disabled={processing}
              data-testid="cancel-without-refund-btn"
            >
              Annuleren zonder terugbetaling
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =================== Order Detail Modal ===================
const OrderDetailModal = ({ order, open, onClose, onPrint, onCancel }) => {
  if (!order) return null;
  const TypeIcon = order.order_type === 'delivery' ? Truck : Utensils;
  const orderTypeLabel = { delivery: 'Bezorging', pickup: 'Afhalen', dine_in: 'Ter Plaatse' }[order.order_type] || order.order_type;
  const paymentLabel = {
    cash: 'Contant bij ontvangst',
    online: 'Online betaald',
    card_on_delivery: 'Pin bij ontvangst',
  }[order.payment_method] || order.payment_method;
  const paymentBadgeClass = order.payment_status === 'paid'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
    : 'bg-amber-100 text-amber-700 border-amber-300';
  const paymentBadgeLabel = order.payment_status === 'paid' ? 'BETAALD' : 'NOG TE BETALEN';
  // Geannuleerd maar het geld staat er nog: dat mag niet wegvallen in een toast
  // die na vijf seconden verdwijnt.
  const terugbetalingOpen = order.status === 'cancelled'
    && order.payment_method === 'online'
    && order.payment_status === 'paid';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="order-detail-modal">
        <DialogHeader>
          {terugbetalingOpen && (
            <div className="bg-red-100 border border-red-300 rounded-lg px-3 py-2 mb-2"
                 data-testid="refund-open-badge">
              <p className="text-sm font-semibold text-red-800">TERUGBETALING OPENSTAAND</p>
              <p className="text-xs text-red-700">
                Deze bestelling is geannuleerd, maar de klant heeft zijn geld nog niet.
                Betaal \u20ac{order.total?.toFixed(2)} terug via Stripe.
              </p>
            </div>
          )}
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1A4D2E] rounded-xl flex items-center justify-center">
              <TypeIcon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              {(() => {
                const isDelivery = order.order_type === 'delivery';
                const addr = order.delivery_address;
                const primary = isDelivery && addr
                  ? `${addr.street || ''} ${addr.house_number || ''}${addr.city ? ', ' + addr.city : ''}`.trim()
                  : (order.customer_name || `#${order.order_number}`);
                return (
                  <>
                    <p className="text-lg font-bold truncate" title={primary}>{primary}</p>
                    <p className="text-xs font-normal text-gray-500 truncate">
                      #{order.order_number} · {orderTypeLabel} · {new Date(order.created_at).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </>
                );
              })()}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Scheduled order banner (if applicable) */}
          {order.scheduled_type === 'scheduled' && order.scheduled_for && (
            <div className="border-2 border-orange-300 bg-orange-50 rounded-xl p-4 flex items-center gap-3" data-testid="detail-scheduled-banner">
              <Clock className="w-6 h-6 text-orange-600 shrink-0" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-orange-700 font-medium">Geplande bestelling</p>
                <p className="text-lg font-bold text-orange-900">
                  {new Date(order.scheduled_for).toLocaleString('nl-BE', {
                    weekday: 'long', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Payment status badge — BIG */}
          <div className={`border-2 rounded-xl p-4 text-center font-bold ${paymentBadgeClass}`} data-testid="detail-payment-status">
            <p className="text-xs uppercase tracking-wider mb-1">Betaling</p>
            <p className="text-lg">{paymentBadgeLabel}</p>
            <p className="text-sm font-normal mt-1">{paymentLabel}</p>
          </div>

          {/* Customer info */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Klant</p>
            <p className="font-semibold text-gray-900" data-testid="detail-customer-name">{order.customer_name}</p>
            <a href={`tel:${order.customer_phone}`} className="flex items-center gap-2 text-[#1A4D2E] hover:underline">
              <Phone className="w-4 h-4" /> {order.customer_phone}
            </a>
            {order.customer_email && (
              <a href={`mailto:${order.customer_email}`} className="flex items-center gap-2 text-gray-600 text-sm hover:underline">
                <span>📧</span> {order.customer_email}
              </a>
            )}
          </div>

          {/* Delivery address */}
          {order.delivery_address && (
            <div className="bg-blue-50 rounded-xl p-4" data-testid="detail-delivery-address">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Bezorgadres</p>
              <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">{order.delivery_address.street} {order.delivery_address.house_number}</p>
                  <p className="text-gray-700">{order.delivery_address.postal_code} {order.delivery_address.city}</p>
                  {order.delivery_address.additional_info && (
                    <p className="text-sm text-gray-600 mt-1 italic">{order.delivery_address.additional_info}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Items */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Bestelling</p>
            </div>
            <ul className="divide-y divide-gray-100" data-testid="detail-items-list">
              {order.items?.map((item, idx) => (
                <li key={idx} className="flex justify-between items-center px-4 py-3">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      <span className="text-[#1A4D2E] font-bold">{item.quantity}×</span> {item.name}
                    </p>
                    {item.special_instructions && (
                      <p className="text-xs text-gray-500 italic mt-0.5">{item.special_instructions}</p>
                    )}
                  </div>
                  <p className="font-mono text-gray-700">{formatPrice(item.price * item.quantity, order.currency)}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Special instructions */}
          {order.special_instructions && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <p className="text-xs font-medium text-yellow-800 uppercase tracking-wider mb-1">Opmerking van klant</p>
              <p className="text-yellow-900">{order.special_instructions}</p>
            </div>
          )}

          {/* Totals */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-1.5">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotaal</span>
              <span>{formatPrice(order.subtotal, order.currency)}</span>
            </div>
            {order.delivery_fee > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Bezorgkosten</span>
                <span>{formatPrice(order.delivery_fee, order.currency)}</span>
              </div>
            )}
            {order.tip_amount > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Fooi</span>
                <span>{formatPrice(order.tip_amount, order.currency)}</span>
              </div>
            )}
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-sm text-emerald-700">
                <span>Korting{order.discount_code ? ` (${order.discount_code})` : ''}</span>
                <span>-{formatPrice(order.discount_amount, order.currency)}</span>
              </div>
            )}
            <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between font-bold text-lg text-gray-900">
              <span>Totaal</span>
              <span data-testid="detail-total">{formatPrice(order.total, order.currency)}</span>
            </div>
          </div>

          {/* Prep time info if confirmed */}
          {order.estimated_ready_time && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center gap-3">
              <Clock className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-xs text-purple-700 uppercase tracking-wider">Klaar om</p>
                <p className="font-bold text-purple-900">
                  {new Date(order.estimated_ready_time).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => { onPrint(order.id); }} data-testid="detail-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Bon
          </Button>
          {order.status !== 'cancelled' && order.status !== 'delivered' && (
            <Button
              variant="destructive"
              onClick={() => onCancel && onCancel(order.id)}
              data-testid="detail-cancel-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              {order.payment_status === 'paid' && order.payment_method === 'online'
                ? 'Annuleren + Terugbetalen'
                : 'Annuleren'}
            </Button>
          )}
          <Button onClick={onClose} className="btn-primary">Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Pending Order Card Component
const PendingOrderCard = ({ order, onAccept, onReject, onPrint, onOpenDetail, prepWarningMinutes = 10 }) => {
  const TypeIcon = order.order_type === 'delivery' ? Truck : Utensils;
  const schedInfo = getScheduledInfo(order, prepWarningMinutes);
  const isPrepUrgent = schedInfo.isScheduled && (schedInfo.isDueSoon || schedInfo.isOverdue);
  // Show address for delivery, customer name for pickup — as PRIMARY
  const isDelivery = order.order_type === 'delivery';
  const addr = order.delivery_address;
  const primary = isDelivery && addr
    ? `${addr.street || ''} ${addr.house_number || ''}${addr.city ? ', ' + addr.city : ''}`.trim()
    : (order.customer_name || `#${order.order_number}`);
  
  return (
    <div
      className={`bg-white rounded-2xl border-2 shadow-lg overflow-hidden animate-fadeIn cursor-pointer hover:shadow-xl transition-shadow ${
        isPrepUrgent ? 'border-red-500 animate-pulse' : 'border-amber-400'
      }`}
      data-testid={`pending-order-${order.id}`}
      onClick={(e) => { if (e.target.closest('button')) return; onOpenDetail(order); }}
    >
      <div className={`px-6 py-3 border-b ${isPrepUrgent ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isPrepUrgent ? 'bg-red-500' : 'bg-amber-500'}`}>
              <TypeIcon className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-base text-gray-900 truncate max-w-[280px]" title={primary} data-testid={`pending-primary-${order.id}`}>
                  {primary}
                </span>
                {!schedInfo.isScheduled && (
                  <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-medium rounded-full animate-pulse shrink-0">
                    NIEUW
                  </span>
                )}
                <ScheduledBadge order={order} prepWarningMinutes={prepWarningMinutes} />
              </div>
              <p className="text-xs text-gray-500 truncate">
                #{order.order_number} · {order.order_type === 'delivery' ? 'Bezorging' : 'Afhalen'}
                {isDelivery && order.customer_name ? ` · ${order.customer_name}` : ''}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-2xl font-bold text-gray-900">{formatPrice(order.total, order.currency)}</p>
            <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Customer Info */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium text-gray-900">{order.customer_name}</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <Phone className="w-4 h-4" />
              {order.customer_phone}
            </span>
          </div>
          {order.delivery_address && (
            <div className="flex items-start gap-1 text-sm text-gray-600 mt-1">
              <MapPin className="w-4 h-4 mt-0.5" />
              <span>
                {order.delivery_address.street} {order.delivery_address.house_number}, {order.delivery_address.postal_code} {order.delivery_address.city}
              </span>
            </div>
          )}
        </div>

        {/* Order Items */}
        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <p className="text-xs font-medium text-gray-500 mb-2">BESTELLING</p>
          <div className="space-y-2">
            {order.items?.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span>
                  <span className="font-medium">{item.quantity}x</span> {item.name}
                </span>
                <span className="text-gray-600">{formatPrice(item.price * item.quantity, order.currency)}</span>
              </div>
            ))}
          </div>
          {order.special_instructions && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-medium text-amber-600">OPMERKING:</p>
              <p className="text-sm text-gray-700">{order.special_instructions}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
            onClick={onReject}
            data-testid="reject-btn"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Weigeren
          </Button>
          <Button
            variant="outline"
            onClick={onPrint}
          >
            <Printer className="w-4 h-4" />
          </Button>
          <Button
            className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            onClick={onAccept}
            data-testid="accept-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Accepteren
          </Button>
        </div>
      </div>
    </div>
  );
};

// Active Order Card Component
const ActiveOrderCard = ({ order, onStatusChange, onPrint, onOpenDetail }) => {
  const TypeIcon = order.order_type === 'delivery' ? Truck : Utensils;
  const nextStatus = order.status === 'confirmed' ? 'preparing' : 'ready';
  const nextStatusLabel = order.status === 'confirmed' ? 'Start Bereiding' : 'Klaar';
  const isDelivery = order.order_type === 'delivery';
  const addr = order.delivery_address;
  const primary = isDelivery && addr
    ? `${addr.street || ''} ${addr.house_number || ''}${addr.city ? ', ' + addr.city : ''}`.trim()
    : (order.customer_name || `#${order.order_number}`);
  
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      data-testid={`active-order-${order.id}`}
      onClick={(e) => { if (e.target.closest('button')) return; onOpenDetail && onOpenDetail(order); }}
    >
      <div className={`px-4 py-2 ${order.status === 'confirmed' ? 'bg-blue-50' : 'bg-purple-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${order.status === 'confirmed' ? 'bg-blue-500' : 'bg-purple-500'}`}></span>
            <span className="font-medium text-sm">
              {order.status === 'confirmed' ? 'Bevestigd' : 'In Bereiding'}
            </span>
          </div>
          {order.estimated_ready_time && (
            <span className="text-xs text-gray-500">
              <Clock className="w-3 h-3 inline mr-1" />
              {new Date(order.estimated_ready_time).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <TypeIcon className="w-4 h-4 text-gray-500 mt-1 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gray-900 truncate" title={primary} data-testid={`active-primary-${order.id}`}>{primary}</p>
              <p className="text-xs text-gray-500 truncate">
                #{order.order_number}{isDelivery && order.customer_name ? ` · ${order.customer_name}` : ''}
              </p>
            </div>
          </div>
          <span className="font-bold shrink-0">{formatPrice(order.total, order.currency)}</span>
        </div>

        <p className="text-xs text-gray-500 mb-3">{order.items?.length || 0} items</p>

        {/* Scheduled badge for active orders */}
        {order.scheduled_type === 'scheduled' && (
          <div className="mb-3">
            <ScheduledBadge order={order} />
          </div>
        )}

        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onPrint}>
            <Printer className="w-4 h-4" />
          </Button>
          <Button 
            size="sm" 
            className={`flex-1 ${order.status === 'confirmed' ? 'bg-purple-500 hover:bg-purple-600' : 'bg-emerald-500 hover:bg-emerald-600'}`}
            onClick={() => onStatusChange(order.id, nextStatus)}
          >
            {nextStatusLabel}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Ready Order Card Component
const ReadyOrderCard = ({ order, onComplete, onOpenDetail }) => {
  const TypeIcon = order.order_type === 'delivery' ? Truck : Utensils;
  
  return (
    <div
      className="bg-emerald-50 rounded-xl border-2 border-emerald-400 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      data-testid={`ready-order-${order.id}`}
      onClick={(e) => { if (e.target.closest('button')) return; onOpenDetail && onOpenDetail(order); }}
    >
      <div className="bg-emerald-100 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="font-medium text-sm text-emerald-800">Klaar</span>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TypeIcon className="w-4 h-4 text-emerald-600" />
            <span className="font-bold">#{order.order_number}</span>
          </div>
          <span className="font-bold">{formatPrice(order.total, order.currency)}</span>
        </div>

        <p className="text-sm text-gray-700 mb-1">{order.customer_name}</p>
        <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
          <Phone className="w-3 h-3" />
          {order.customer_phone}
        </p>
        
        {order.order_type === 'delivery' && order.delivery_address && (
          <p className="text-xs text-gray-500 mb-3 flex items-start gap-1">
            <MapPin className="w-3 h-3 mt-0.5" />
            {order.delivery_address.street} {order.delivery_address.house_number}
          </p>
        )}

        <Button 
          size="sm" 
          className="w-full bg-emerald-500 hover:bg-emerald-600"
          onClick={onComplete}
        >
          {order.order_type === 'delivery' ? 'Bezorgd' : 'Opgehaald'}
          <CheckCircle className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};
